import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './AuthContext';
import Navigation from './components/Navigation';
import Sidebar from './components/Sidebar';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import MyFiles from './pages/MyFiles';
import PublicFiles from './pages/PublicFiles';
import APKBuilds from './pages/APKBuilds';
import APIKeys from './pages/APIKeys';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import BrowseProjects from './pages/BrowseProjects';
import PublicProjectPage from './pages/PublicProjectPage';
import ProjectLegalPage from './pages/ProjectLegalPage';
import DeveloperDocs from './pages/DeveloperDocs';
import StaticSites from './pages/StaticSites';
import MyBackedProjects from './pages/MyBackedProjects';
import Admin from './pages/Admin';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { authenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return authenticated ? children : <Navigate to="/login" />;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { authenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!authenticated) {
    return <Navigate to="/login" />;
  }

  // Check if user is admin (sirajrazick)
  if (!user || !user.is_admin) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Public Route (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { authenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return authenticated ? <Navigate to="/dashboard" /> : children;
};

function AppContent() {
  const { authenticated } = useAuth();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleMenuClick = () => {
    console.log('Hamburger clicked, current state:', sidebarOpen);
    setSidebarOpen(prev => !prev);
  };

  React.useEffect(() => {
    console.log('Sidebar state changed:', sidebarOpen);
  }, [sidebarOpen]);

  return (
    <Router>
      <Navigation onMenuClick={handleMenuClick} />
      <div className="app-layout">
        {authenticated && <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />}
        {/* Close sidebar when clicking outside on mobile */}
        <main className={authenticated ? "main-content" : "main-content-full"}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-files"
              element={
                <ProtectedRoute>
                  <MyFiles />
                </ProtectedRoute>
              }
            />
            <Route
              path="/apk-builds"
              element={
                <ProtectedRoute>
                  <APKBuilds />
                </ProtectedRoute>
              }
            />
            <Route
              path="/api-keys"
              element={
                <ProtectedRoute>
                  <APIKeys />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <ProtectedRoute>
                  <Projects />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId"
              element={
                <ProtectedRoute>
                  <ProjectDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/backed-projects"
              element={
                <ProtectedRoute>
                  <MyBackedProjects />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <Admin />
                </AdminRoute>
              }
            />
            <Route path="/public-files" element={<PublicFiles />} />
            <Route path="/browse" element={<BrowseProjects />} />
            <Route path="/docs" element={<DeveloperDocs />} />
            <Route path="/static-sites" element={<StaticSites />} />
            <Route path="/p/:slug" element={<PublicProjectPage />} />
            <Route path="/p/:slug/privacy" element={<ProjectLegalPage docType="privacy" />} />
            <Route path="/p/:slug/terms" element={<ProjectLegalPage docType="terms" />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
